import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { PictureAsPdf } from '@mui/icons-material';
import { env } from '@/utils/env';

interface PdfExportButtonProps {
  htmlContent: string;
  filename?: string;
  disabled?: boolean;
}

export const PdfExportButton: React.FC<PdfExportButtonProps> = ({
  htmlContent,
  filename = 'contrato.pdf',
  disabled = false
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const cleanHtmlForPdf = (html: string): string => {
    // Converter divs de quebra de página do editor em quebras CSS para PDF
    let cleanedHtml = html;
    
    // Converter divs de quebra de página em elementos com CSS de quebra
    cleanedHtml = cleanedHtml.replace(
      /<div class="page-break"[^>]*>[\s\S]*?<\/div>/gi,
      '<div style="page-break-before: always; break-before: page; height: 0; margin: 0; padding: 0;"></div>'
    );
    
    // Padrão alternativo caso use aspas simples
    cleanedHtml = cleanedHtml.replace(
      /<div class='page-break'[^>]*>[\s\S]*?<\/div>/gi,
      '<div style="page-break-before: always; break-before: page; height: 0; margin: 0; padding: 0;"></div>'
    );
    
    // Remover quebras de linha extras que podem sobrar
    cleanedHtml = cleanedHtml.replace(/\n\s*\n\s*\n/g, '\n\n');
    
    console.log('🔄 HTML processado para PDF (quebras de página convertidas)');
    return cleanedHtml;
  };

  const handleExportPdf = async () => {
    if (!htmlContent.trim()) {
      alert('Nenhum conteúdo disponível para exportar.');
      return;
    }

    setIsGenerating(true);

    try {
      console.log('🚀 Enviando HTML para geração de PDF...');
      
      // Limpar HTML antes de enviar
      const cleanedHtml = cleanHtmlForPdf(htmlContent);

      const response = await fetch(`${env.API_URL}/api/pdf/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          html: cleanedHtml,
          filename 
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erro HTTP: ${response.status}`);
      }

      // Verificar se a resposta é realmente um PDF
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/pdf')) {
        throw new Error('Resposta não é um PDF válido');
      }

      // Converter resposta para blob com tipo específico
      const blob = await response.blob();
      
      // Verificar se o blob tem conteúdo
      if (blob.size === 0) {
        throw new Error('PDF gerado está vazio');
      }
      
      console.log('📄 PDF recebido, tamanho:', blob.size, 'bytes');
      
      // Criar blob com tipo específico para PDF
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      
      // Criar URL temporária e fazer download
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      // Adicionar ao DOM, clicar e remover
      document.body.appendChild(link);
      link.click();
      
      // Limpar recursos após um pequeno delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      console.log('✅ PDF baixado com sucesso!');

    } catch (error: any) {
      console.error('❌ Erro ao gerar PDF:', error);
      alert(`Erro ao gerar PDF: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
      onClick={handleExportPdf}
      disabled={disabled || isGenerating || !htmlContent.trim()}
      sx={{
        minWidth: '140px',
        height: '40px'
      }}
    >
      {isGenerating ? 'Gerando...' : 'Exportar PDF'}
    </Button>
  );
}; 